import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useLanguage, useT } from '../i18n/useLanguage';
import { API_BASE } from '../lib/apiBase';
import { useRegisterModal } from '../contexts/RegisterModalContext';
import { trackEvent, trackStoreClick } from '../lib/analytics';
import { appStoreUrl, googlePlayUrl } from '../lib/storeLinks';

interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = 'form' | 'success';
type FormStatus =
  | 'idle'
  | 'submitting'
  | 'errorDuplicate'
  | 'errorInvalid'
  | 'errorClosed'
  | 'errorGeneric';

// Pre-registration modal — Figma 120:2230 (form) + 216:156 (success).
// Same chrome for both steps (white panel, 10px white inner border,
// 30px corner radius, chunky 13px drop shadow, diamond-tile background).
// Step 1 collects the email and POSTs to `/api/register` (see
// apps/api/README.md for the contract): 201 → success step, 409 →
// duplicate-email error, 400 → invalid-email error, anything else →
// generic retry error. Step 2 thanks the user and nudges them to
// install the app.
export default function RegisterModal({ open, onClose }: RegisterModalProps) {
  const t = useT();
  // Pulls the shared participantCount setter so a 201 response can
  // push the new total straight into RegisterSection (no second
  // /api/count round trip, and we sidestep the 60s edge cache on
  // /count).
  const { setParticipantCount } = useRegisterModal();
  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  // Close on ESC + lock body scroll while the modal is open so the
  // page's snap-scroll doesn't fight touch input inside the modal.
  // Mirrors JoinModal.tsx so the two campaign modals behave the same.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Reset to the form step + clear the email + clear any submit
  // status whenever the modal closes, so the next open always starts
  // fresh. Delayed slightly so the reset doesn't flash before the
  // close transition completes.
  useEffect(() => {
    if (open) return;
    const id = window.setTimeout(() => {
      setStep('form');
      setEmail('');
      setStatus('idle');
    }, 200);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    // Submit-attempt event. Fires once per real attempt (the
    // `status === 'submitting'` guard above prevents double-clicks
    // from counting twice). Independent of whether the API call
    // ultimately succeeds — this measures intent.
    trackEvent(
      { name: 'ClickPreRegisterWeb' },
      { name: 'click_pre_register_web' },
    );
    setStatus('submitting');
    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ email }),
      });
      if (res.status === 201) {
        // 201 body shape: { ok: true, count: number } — see
        // apps/api/src/routes/register.ts. Push the new total into
        // the shared context so RegisterSection updates the moment
        // the user closes the success step.
        try {
          const data = (await res.json()) as { count?: unknown };
          if (
            typeof data?.count === 'number' &&
            Number.isFinite(data.count)
          ) {
            setParticipantCount(data.count);
          }
        } catch {
          // Body wasn't valid JSON — ignore, UI still moves on.
        }
        setStatus('idle');
        setStep('success');
        // Standard `CompleteRegistration` for Meta (recognised by
        // their ML optimisation) + `sign_up` for GA4 (the
        // recommended event name for account creation, with
        // `method: 'email'` to distinguish from social sign-ups).
        // Only fires on 201 — duplicate/invalid/error paths skip
        // this so conversion numbers stay clean.
        trackEvent(
          {
            name: 'CompleteRegistration',
            standard: true,
            params: { method: 'email' },
          },
          { name: 'sign_up', params: { method: 'email' } },
        );
        return;
      }
      if (res.status === 409) {
        setStatus('errorDuplicate');
        return;
      }
      if (res.status === 400) {
        setStatus('errorInvalid');
        return;
      }
      if (res.status === 403) {
        // Registration closed between page load and submit (the count
        // fetch that grays the CTAs is edge-cached up to 60s, so this
        // window exists). The API's 403 is the authoritative gate.
        setStatus('errorClosed');
        return;
      }
      setStatus('errorGeneric');
    } catch {
      setStatus('errorGeneric');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('register.modal.title')}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/90 px-4 py-6"
    >
      {/* Panel — Figma 120:2230 / 216:156. Both steps share the same
          shell so swapping them feels like the modal's contents
          shift rather than a new dialog opening. Width caps at 620px
          (Figma); aspect-ratio is locked to 620/640 ≈ 0.97 so the
          composition reads the same regardless of viewport width.
          On very short screens `maxHeight` clamps before the aspect-
          ratio rule does, and the inner content scales down with
          container queries. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[620px] overflow-hidden rounded-[30px] border-[10px] border-white bg-white shadow-[0_13px_0_0_rgba(0,0,0,0.25)] [container-type:inline-size]"
        style={{
          aspectRatio: '620 / 640',
          maxHeight: 'min(92vh, 720px)',
        }}
      >
        {/* Diamond pattern wash — register-modal-bg.svg paints the
            faded pink diamond grid that fills the entire panel
            interior. Uses `cover` so the pattern stays edge-to-edge
            at any modal size. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "url('/images/register-modal-bg.svg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Close X — matches JoinModal so both modals use the same
            cooking-iconography close button. Sized in cqw so it
            scales with the modal rather than the viewport. */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('register.modal.close')}
          className="absolute right-[3.5%] top-[3.5%] z-30 transition hover:scale-110 active:scale-95"
        >
          <img
            src="/images/icon-close-modal.png"
            alt=""
            aria-hidden="true"
            className="block h-9 w-9 select-none md:h-11 md:w-11"
            draggable={false}
          />
        </button>

        {step === 'form' ? (
          <FormStep
            email={email}
            setEmail={setEmail}
            onSubmit={handleSubmit}
            status={status}
          />
        ) : (
          <SuccessStep />
        )}
      </div>
    </div>
  );
}

interface FormStepProps {
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  status: FormStatus;
}

// Step 1 — Figma 120:2230. Logo, "ลงทะเบียน" title, email input and
// the wooden-plank CTA stacked vertically. All cqw values map back
// to the Figma px coords assuming the modal is its design width of
// 620px (1cqw = 6.2px), so the proportions stay identical at any
// rendered size.
function FormStep({ email, setEmail, onSubmit, status }: FormStepProps) {
  const { lang, t } = useLanguage();
  const submitting = status === 'submitting';
  const errorKey =
    status === 'errorDuplicate'
      ? 'register.modal.errorDuplicate'
      : status === 'errorInvalid'
        ? 'register.modal.errorInvalid'
        : status === 'errorClosed'
          ? 'register.modal.errorClosed'
          : status === 'errorGeneric'
            ? 'register.modal.errorGeneric'
            : null;

  return (
    <form
      onSubmit={onSubmit}
      className="relative z-10 flex h-full w-full flex-col items-center"
    >
      {/* Logo — Figma 120:2240 (top 32 / 210×122). Top padding is
          5.16cqw (32px @ design width). */}
      <img
        src="/images/logo.png"
        alt="I Love Cooking"
        className="mt-[5.16cqw] block h-auto w-[33.9cqw] select-none"
        draggable={false}
      />

      {/* Title "ลงทะเบียน" — Figma 120:2242 (32px, #634645). The
          gap-15px between logo and title in Figma collapses to
          2.4cqw of top margin here. */}
      <h2 className="mt-[2.4cqw] m-0 text-center font-thai font-bold leading-none text-section-feature text-[5.16cqw]">
        {t('register.modal.title')}
      </h2>

      <div className="mt-[2.4cqw] flex w-[81.5cqw] flex-col items-center gap-[1.6cqw]">
        <p className="flex items-start justify-start w-full text-center font-thai text-[3cqw] font-bold leading-none text-section-feature">
          {t('register.modal.storeSubtitle')}
        </p>
        <div className="flex w-full items-center justify-center gap-[2cqw]">
          <a
            href={appStoreUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
            onClick={() => trackStoreClick('apple')}
            className="block flex-1 transition hover:scale-[1.03] active:scale-95"
          >
            <img
              src="/images/store-app-store.png"
              alt="Download on the App Store"
              className="block h-auto w-full select-none"
              draggable={false}
            />
          </a>
          <a
            href={googlePlayUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
            onClick={() => trackStoreClick('google')}
            className="block flex-1 transition hover:scale-[1.03] active:scale-95"
          >
            <img
              src="/images/store-google-play.png"
              alt="Get it on Google Play"
              className="block h-auto w-full select-none"
              draggable={false}
            />
          </a>
        </div>
      </div>

      {/* Email input — Figma 120:2245 (505×60, 20px radius, 3px
          rose-tinted border, #ebebeb fill). Width caps at 81.5cqw
          to match the input pill's width, and sits at top 47.9cqw
          (297px in Figma) via auto top margin from the flex column.
          Border flashes coral red (`primary-strong`, the design
          system's red pastel — see styles/tokens.css) on error to
          draw the user's eye back to the input. */}
      <div className="mt-[5cqw] w-[81.5cqw]">
        <p className="flex items-start justify-start w-full text-center font-thai text-[3cqw] font-bold leading-none text-section-feature">
          {t('register.modal.emailSubtitle')}
        </p>
        <input
          type="email"
          required
          disabled={submitting}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('register.modal.emailPlaceholder')}
          aria-label={t('register.modal.emailPlaceholder')}
          aria-invalid={errorKey !== null}
          className={`mt-1 block w-full rounded-[3.2cqw] border-[3px] bg-[#ebebeb] px-[6.45cqw] py-[2.4cqw] font-thai font-bold text-[3.87cqw] leading-[1.2] text-section-feature placeholder:text-[#838383] focus:border-section-feature focus:outline-none disabled:opacity-60 ${
            errorKey
              ? 'border-primary-strong'
              : 'border-[rgba(92,29,52,0.2)]'
          }`}
        />
      </div>

      {/* Inline status line — reserves space so the button below
          doesn't jump when an error appears. Errors paint in coral
          red (`primary-strong`); the submitting hint stays in the
          muted section-feature brown so it doesn't read as a
          warning. Idle state collapses to a blank placeholder line. */}
      <p
        role={errorKey ? 'alert' : undefined}
        aria-live="polite"
        className={`mt-[1.6cqw] min-h-[3.5cqw] w-[81.5cqw] text-center font-thai font-bold text-[2.6cqw] leading-tight ${
          errorKey ? 'text-primary-strong' : 'text-section-feature/70'
        }`}
      >
        {errorKey
          ? t(errorKey)
          : submitting
            ? t('register.modal.submitting')
            : ' '}
      </p>

      {/* Submit — the same wooden-plank cta-join.png used in
          HeroSection / FloatingCTARail / RegisterSection so every
          "ลงทะเบียน" button on the page reads as the same action.
          Width is 56.1cqw to match the Figma button's 348px at
          design width. Dimmed + non-interactive while the
          /api/register request is in flight. */}
      <button
        type="submit"
        disabled={submitting}
        aria-label={t('register.modal.submit')}
        aria-busy={submitting}
        className="mt-[3cqw] block w-fit transition hover:scale-105 active:scale-95 disabled:cursor-wait disabled:opacity-60 disabled:hover:scale-100"
      >
        <img
          src={`/images/cta-join-${lang}.png`}
          alt=""
          aria-hidden="true"
          className="block h-auto w-[56.1cqw] select-none"
          draggable={false}
        />
      </button>
    </form>
  );
}

// Step 2 — Figma 216:156. Composition is layered rather than
// stacked, so we position each piece absolutely as a % of the modal
// interior to match the artwork's intentional overlap (the maid
// character's right arm tucks UNDER the ILC logo / store badges,
// and the speech bubble's tail points DOWN at her head). All cqw
// values map back to the Figma px coords assuming the modal is its
// design width of 620px:
//   1cqw = 6.2px @ design width.
function SuccessStep() {
  const { lang, t } = useLanguage();

  return (
    <div className="relative z-10 h-full w-full">
      {/* Speech bubble — Figma 216:159 (top 89 / left 51 / 497×202,
          rotated 2.67°). `success-banner.svg` already carries the
          white fill + rose stroke + downward tail, so this is just
          the image positioned in the top-left quadrant with the
          thank-you copy overlaid in `text-section-feature` brown. */}
      <div className="absolute left-[8.2cqw] top-[14.4cqw] w-[80.2cqw]">
        <div className="relative rotate-[2.67deg]">
          <img
            src="/images/success-banner.svg"
            alt=""
            aria-hidden="true"
            className="block h-auto w-full select-none"
            draggable={false}
          />
          {/* Thank-you copy — three lines. Middle line + "บน Store"
              jump from 24px → 30px for emphasis (Figma sizes
              converted to 3.87cqw and 4.84cqw at 620px design
              width). `pb-[8cqw]` pulls the copy off the bubble's
              downward tail so it stays centred in the rounded body. */}
          <div className="pointer-events-none absolute mt-6 inset-0 flex flex-col items-center justify-center pb-[8cqw] text-center font-thai font-bold leading-[1.3] text-section-feature">
            <span className="text-[3.87cqw]">{t('register.modal.successLine1')}</span>
            <span className="text-[4.84cqw]">{t('register.modal.successLine2')}</span>
            <span className="text-[3.87cqw]">
              <span className="text-[4.84cqw]">{t('register.modal.successLine3Store')}</span>
              {t('register.modal.successLine3Suffix')}
            </span>
          </div>
        </div>
      </div>

      {/* Maid mascot — Figma 216:166 (top 254 / left -1 / 349×364).
          Anchored to the bottom-left of the modal so she sits flush
          with the panel edge. The "สำเร็จ" badge is overlaid on
          the blank paper she's holding, rotated 8.67° to follow
          the paper's tilt. */}
      <div className="absolute bottom-[3.5cqw] left-0 w-[56.3cqw]">
        <img
          src="/images/success-done.png"
          alt=""
          aria-hidden="true"
          className="block h-auto w-full select-none"
          draggable={false}
        />
        {/* "สำเร็จ" — sits on the paper at roughly 64% width / 28%
            height of the mascot illustration. Coordinates are tuned
            to the success-done.png artwork (blank notepad she's
            holding up in her left hand). */}
        <span
          aria-hidden="true"
          className="absolute left-[86%] top-[30%] -translate-x-1/2 -translate-y-1/2 rotate-[8.67deg] font-thai font-bold text-section-feature text-[4.2cqw] leading-none"
        >
          {t('register.modal.successBadge')}
        </span>
      </div>

      {/* Right column — Figma 216:168. ILC logo on top, then the
          Google Play + App Store badges stacked. Same assets as
          HeroSection / FloatingCTARail so a user already familiar
          with the store buttons elsewhere on the page recognises
          them instantly. Right offset (12cqw) matches Figma so the
          column hugs the same imaginary right gutter as the modal's
          padding. */}
      <div className="absolute bottom-[12.9cqw] right-[12cqw] flex w-[31.9cqw] flex-col items-center gap-[3.2cqw]">
        <img
          src="/images/logo.png"
          alt="I Love Cooking"
          className="block h-auto w-[23.5cqw] select-none"
          draggable={false}
        />
        <a
          href={appStoreUrl(lang)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
          onClick={() => trackStoreClick('apple')}
          className="block w-full transition hover:scale-[1.03] active:scale-95"
        >
          <img
            src="/images/store-app-store.png"
            alt="Download on the App Store"
            className="block h-auto w-full select-none"
            draggable={false}
          />
        </a>
        <a
          href={googlePlayUrl(lang)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          onClick={() => trackStoreClick('google')}
          className="block w-full transition hover:scale-[1.03] active:scale-95"
        >
          <img
            src="/images/store-google-play.png"
            alt="Get it on Google Play"
            className="block h-auto w-full select-none"
            draggable={false}
          />
        </a>
      </div>
    </div>
  );
}
